import pako from 'pako';
import _ from 'lodash';
import fetch from 'isomorphic-unfetch';

import * as Model from '../model/types';
import {unpineapple} from '../pineapple';
import {serialize} from '../serialize';
import {Server} from './types';

const BATCH_INTERVAL_MS = 100;

// from https://www.jpwilliams.dev/how-to-unpack-the-return-type-of-a-promise-in-typescript
// when all of our apps are on TS 4.x we can use Awaited<> instead
type Unwrap<T> = T extends Promise<infer U>
  ? U
  : T extends (...args: any) => Promise<infer U2>
  ? U2
  : T extends (...args: any) => infer U3
  ? U3
  : T;

// to prevent importing the DOM types here (they're all or nothing),
// we get the Response type off of the fetch polyfill instead
//
// because the library doesn't actually export it, we need to unpack the promise
// type returned by the fetch() function
type Response = Unwrap<ReturnType<typeof fetch>>;

export interface RemoteWeaveOptions {
  weaveUrl: string;

  // An async function for retrieving auth token
  tokenFunc: () => Promise<string | undefined>;

  useAdminPrivileges: boolean;
  useGzip: boolean;
}

const defaultOpts: RemoteWeaveOptions = {
  weaveUrl: 'https://localhost:9004/execute',
  tokenFunc: () => Promise.resolve(''),
  useAdminPrivileges: false,
  useGzip: false,
};

// Handles (de)serialization to send to a remote CG server
export class RemoteHttpServer implements Server {
  private pendingRequests: Array<[Model.Node, (r: any) => void]> = [];
  private readonly opts: RemoteWeaveOptions;

  private flush = _.throttle(
    async (useGzip: boolean, retryNum = 0) => {
      const requests = [...this.pendingRequests];
      this.pendingRequests.length = 0;

      const payload = JSON.stringify({
        graphs: serialize(requests.map(r => r[0])),
      });

      // Compression
      let body: string | Uint8Array = payload;
      const additionalHeaders: Record<string, string> = {};
      if (payload.length > 1024 && useGzip) {
        const originalLength = payload.length;
        body = pako.gzip(payload);
        console.debug(
          `[GZIP] Compressed ${requests.length} graphs ${Math.round(
            (1 - body.length / originalLength) * 100
          )}% (${originalLength} -> ${body.length} bytes)`
        );
        additionalHeaders['Content-Encoding'] = 'gzip';
      }

      // This is also found in cookie, which to prefer?
      if (this.opts.useAdminPrivileges) {
        additionalHeaders['use-admin-privileges'] = 'true';
      }

      let response: Response | null = null;
      try {
        response = await fetch(this.opts.weaveUrl, {
          credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...additionalHeaders,
          },
          body,
        });
      } catch (err) {
        // TODO(np): Uncrapify: We are just requeuing failed requests then scheduling a flush w/
        // exponential backoff, up to around ~1 min/retry.  We never give up.
        const retryWait = 500 * Math.exp(Math.min(retryNum, 10) / 2);
        this.pendingRequests.push.apply(this.pendingRequests, requests);
        setTimeout(() => this.flush(useGzip, ++retryNum), retryWait);
        console.error(
          `[REMOTE CG] retry ${requests.length} graphs in ${(
            retryWait / 1000
          ).toFixed(2)}s because fetch failed: ${(err as Error).message}`
        );
        return;
      }

      const respJson = await response.json();
      const values = unpineapple(respJson.data);

      for (let i = 0; i < requests.length; i++) {
        // console.log(`result`, values[i]);
        requests[i][1](values[i]);
      }
    },
    BATCH_INTERVAL_MS,
    {leading: false, trailing: true}
  );

  public constructor(inOpts: Partial<RemoteWeaveOptions>) {
    this.opts = _.defaults({}, inOpts, defaultOpts);
  }

  public async query(nodes: Model.Node[]): Promise<any[]> {
    return await Promise.all(
      nodes.map(
        node =>
          new Promise((resolve /* reject */) => {
            this.pendingRequests.push([node, resolve]);
            this.flush(this.opts.useGzip);
          })
      )
    );
  }
}
